/*
 * Copyright (c) 2022, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */
import { ASGARDEO_STATE_SUFFIX_PATTERN } from 'data/constants';
import { ProtectedRouteStateParam } from 'types/route';
import { getCookie, setCookie, deleteCookie } from 'utils/browser';
import { clearAllCopilotChats } from 'utils/copilotChatStorage';
import logger from './logger';

export const ASGARDEO_ISK_SESSION_KEY = 'asgardeo_isk';
export const RETURN_TO_URL = 'returnToUrl';
export const RETURN_TO_ORG = 'returnToOrg';
export const CONFIRM_ORG = 'confirmationOrg';
export const IDP_ID_SESSION_KEY = 'fidpId';
export const AZURE_MARKETPLACE_TOKEN = 'azureMarketplaceToken';
export const GCP_MARKETPLACE_CUSTOMER_ID = 'gcpMarketplaceCustomerId';
export const AWS_MARKETPLACE_CUSTOMER_ID = 'awsMarketplaceCustomerId';
export const AWS_ATTACHED_ORG_ID = 'awsAttachedOrgId';

export const setAsgardeoISK = (isk: string | boolean) => {
  if (isk === false) {
    sessionStorage.removeItem(ASGARDEO_ISK_SESSION_KEY);
  } else {
    sessionStorage.setItem(ASGARDEO_ISK_SESSION_KEY, isk as string);
  }
};
export const getAsgardeoISK = () =>
  sessionStorage.getItem(ASGARDEO_ISK_SESSION_KEY);

export const getSignedInStateParamObject = (search: string) => {
  let stateQueryParam = new URLSearchParams(search).get('state') || '';
  let decodedStateQueryParam = '';
  stateQueryParam = stateQueryParam.replace(ASGARDEO_STATE_SUFFIX_PATTERN, '');
  try {
    decodedStateQueryParam = atob(stateQueryParam);
  } catch (e) {
    logger.error('Failed to decode state query param', e);
    decodedStateQueryParam = stateQueryParam;
  }

  const stateParamJson: ProtectedRouteStateParam | null =
    decodedStateQueryParam.includes(CONFIRM_ORG) ||
    decodedStateQueryParam.includes(RETURN_TO_URL) ||
    decodedStateQueryParam.includes(RETURN_TO_ORG) ||
    hasGrwowthHackingParams(decodedStateQueryParam)
      ? JSON.parse(decodedStateQueryParam)
      : null;

  return stateParamJson;
};

function hasGrwowthHackingParams(decodedStateQueryParam: string): boolean {
  return (
    decodedStateQueryParam.includes('utm') ||
    decodedStateQueryParam.includes('visitor') ||
    decodedStateQueryParam.includes('profile')
  );
}

export const getFidpId = () => getCookie(IDP_ID_SESSION_KEY);

export const setFidpId = (fidpId: string | undefined) => {
  if (fidpId) {
    setCookie(IDP_ID_SESSION_KEY, fidpId);
  }
};

export const removeFidpId = () => {
  deleteCookie(IDP_ID_SESSION_KEY);
};

export type ChoreoSignInConfig = {
  fidp: string;
  state?: string;
  captchaInfo?: string;
  username?: string;
  initiatedPlatform?: string;
  registerMailTemplateName?: string;
  postRegisterCallbackUrl?: string;
  tenantUrlPrefix?: string;
};

export type VSCodeChoreoSignInConfig = {
  fidp: string;
  state?: string;
  captchaInfo?: string;
  username?: string;
  clientID: string;
  codeChallengeVsCode: string;
};

export const getAzureMarketplaceToken = () =>
  getCookie(AZURE_MARKETPLACE_TOKEN);

export const setAzureMarketplaceToken = (token: string | null | undefined) => {
  if (token) {
    setCookie(AZURE_MARKETPLACE_TOKEN, token);
  }
};

export const removeAzureMarketplaceToken = () => {
  deleteCookie(AZURE_MARKETPLACE_TOKEN);
};

export const getGcpMarketplaceCustomerId = () =>
  getCookie(GCP_MARKETPLACE_CUSTOMER_ID);

export const setGcpMarketplaceCustomerId = (
  token: string | null | undefined
) => {
  if (token) {
    setCookie(GCP_MARKETPLACE_CUSTOMER_ID, token);
  }
};

export const removeGcpMarketplaceCustomerId = () => {
  deleteCookie(GCP_MARKETPLACE_CUSTOMER_ID);
};

export const getAwsMarketplaceCustomerId = () =>
  getCookie(AWS_MARKETPLACE_CUSTOMER_ID);

export const setAwsMarketplaceCustomerId = (
  token: string | null | undefined
) => {
  if (token) {
    setCookie(AWS_MARKETPLACE_CUSTOMER_ID, token);
  }
};

export const removeAwsMarketplaceCustomerId = () => {
  deleteCookie(AWS_MARKETPLACE_CUSTOMER_ID);
};

export const getAwsAttachedOrgId = () => getCookie(AWS_ATTACHED_ORG_ID);

export const setAwsAttachedOrgId = (token: string | null | undefined) => {
  if (token) {
    setCookie(AWS_ATTACHED_ORG_ID, token);
  }
};

export const removeAwsAttachedOrgId = () => {
  deleteCookie(AWS_ATTACHED_ORG_ID);
};

/**
 * The Copilot session id lives with the conversation it belongs to, in
 * `copilotChatStorage` — see the note there on why the pair is stored together
 * and why it is `localStorage`.
 *
 * Ends every stored Copilot conversation on this browser: transcripts go with
 * their session ids, for every user and organization. Signing out calls this,
 * so nothing is left for whoever signs in next.
 */
export const removeChoreoCopilotSessionId = () => {
  clearAllCopilotChats();
};
